/**
 * @inferrex/sdk — HTTP transport layer
 *
 * Handles authentication, timeouts, error parsing, and response typing.
 * Used by all resource clients. Never exposed directly to consumers.
 */

import type { InferrexClientConfig, InferrexApiError } from '@inferrex/contracts';

const DEFAULT_BASE_URL = 'https://api.inferrex.com';
const DEFAULT_TIMEOUT_MS = 30_000;

/** The header a write carries so a retry of the same operation is replayed, not re-applied. */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/**
 * Per-call options for one request. Generated resource methods take this as their last
 * argument on every route that accepts an `Idempotency-Key` (an authenticated write; see the
 * frontend contract's "Retries" line), and pass it straight through.
 */
export interface RequestOptions {
  /**
   * One key per logical operation. Send the SAME key when retrying that operation: the API
   * replays the first answer instead of applying the write twice. A different request body
   * under the same key is refused (409 `idempotency_key_reused`).
   */
  readonly idempotencyKey?: string | undefined;
}

export class InferrexHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: InferrexApiError,
  ) {
    super(body.message ?? body.error);
    this.name = 'InferrexHttpError';
  }
}

export class InferrexTimeoutError extends Error {
  constructor(public readonly url: string, public readonly timeoutMs: number) {
    super(`Request to ${url} timed out after ${timeoutMs}ms`);
    this.name = 'InferrexTimeoutError';
  }
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeoutMs: number;
  private readonly fetchFn: typeof globalThis.fetch;

  constructor(config: InferrexClientConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, '');
    this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.fetchFn = config.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>,
    options?: RequestOptions,
  ): Promise<T> {
    let url = `${this.baseUrl}${path}`;

    if (query) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) params.set(k, String(v));
      }
      const qs = params.toString();
      if (qs) url += `?${qs}`;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const headers: Record<string, string> = {
        'X-API-Key': this.apiKey,
        'Accept': 'application/json',
      };
      if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }
      if (options?.idempotencyKey) {
        headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;
      }

      const init: RequestInit = {
        method,
        headers,
        signal: controller.signal,
      };
      if (body !== undefined) {
        init.body = JSON.stringify(body);
      }

      const response = await this.fetchFn(url, init);

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({
          error: 'unknown_error',
          message: response.statusText,
        })) as InferrexApiError;
        throw new InferrexHttpError(response.status, errorBody);
      }

      return await response.json() as T;
    } catch (err: unknown) {
      if (err instanceof InferrexHttpError) throw err;
      if (controller.signal.aborted) throw new InferrexTimeoutError(url, this.timeoutMs);
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  async get<T>(path: string, query?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>('GET', path, undefined, query);
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, undefined, options);
  }
}
